// PR-3634 in-app trial pop-ups (product manager's flow, 02-Nov): 1 welcome (first load on the trial),
// 2 five days left, 3 last day, 4 trial ended - each shown once. Decided once per app load from
// FeatureTier/GetCurrentPlan (tiers.currentPlan incl. ProTrialExpiredEndDate + proTrial).
// Mounted once in App.js next to ProTrialPlansLauncher; renders nothing otherwise.
import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import TierPlans from '../TierPlans/TierPlans';
import { UserRoles } from '../../Models/SubUser/SubUsers';
import { getCookie } from '../../helpers/Functions/cookies';
import { DateFormats } from '../../helpers/Constants';
import { sitePrefix } from '../../config';
import { selectProTrialInfo, selectProTrialLoaded } from '../../redux/reducers/ProTrialSlice';
import { hasOpenPlansRequest } from './proTrialDeepLink';
import {
  decideProTrialReminder,
  getReminderAccountKey,
  ProTrialReminder,
  ProTrialReminderMemory,
  readReminderMemory,
  writeReminderMemory,
} from './proTrialReminderRules';
import proMascot from '../../assets/images/proTrial/pro-mascot.webp';

// Product manager's design tokens
const GRAD = 'linear-gradient(90deg, #FF0076, #FF0054 50%, #FF4D2A)';
const INK = '#1D1B2C';
const MUTED = '#6E6E7D';
const SOFT = '#F7F7FB';
const PINK = '#FF0054';
const LIME = '#CCFF00';

const useStyles = makeStyles(() => ({
  '@keyframes proTrialPulse': { '50%': { opacity: 0.35 } },
  '@keyframes proTrialBob': {
    '0%, 100%': { transform: 'translateY(0) rotate(-1.5deg)' },
    '50%': { transform: 'translateY(-8px) rotate(1.5deg)' },
  },
  '@keyframes proTrialRing': {
    from: { transform: 'scale(.9)', opacity: 1 },
    to: { transform: 'scale(1.35)', opacity: 0 },
  },
  '@keyframes proTrialNudge': {
    '0%, 100%': { transform: 'none' },
    '25%': { transform: 'rotate(-1.5deg)' },
    '75%': { transform: 'rotate(1.5deg)' },
  },
  paper: {
    width: 440,
    maxWidth: 'calc(100% - 24px)',
    margin: 12,
    maxHeight: 'calc(100% - 24px)',
    borderRadius: 28,
    // never cut off: scrolls inside when the window is shorter than the card
    overflowX: 'hidden',
    overflowY: 'auto',
    position: 'relative',
    boxShadow: '0 40px 90px -30px rgba(29,27,44,.6)',
  },
  urgent: {
    animation: '$proTrialNudge .5s .4s ease-in-out',
  },
  close: {
    position: 'absolute',
    top: 14,
    insetInlineEnd: 14,
    width: 32,
    height: 32,
    borderRadius: '50%',
    border: 0,
    background: 'rgba(255,255,255,.22)',
    color: '#fff',
    display: 'grid',
    placeItems: 'center',
    cursor: 'pointer',
    zIndex: 2,
    padding: 0,
    '&:hover': { background: 'rgba(255,255,255,.4)' },
    '& svg': { width: 15, height: 15 },
  },
  header: {
    position: 'relative',
    background: GRAD,
    padding: '30px 24px 26px',
    textAlign: 'center',
    color: '#fff',
    isolation: 'isolate',
    overflow: 'hidden',
    '&:before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      backgroundImage: 'radial-gradient(rgba(255,255,255,.22) 1px, transparent 1px)',
      backgroundSize: '18px 18px',
      zIndex: -1,
      maskImage: 'radial-gradient(circle at center, #000, transparent 75%)',
      WebkitMaskImage: 'radial-gradient(circle at center, #000, transparent 75%)',
    },
  },
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    fontSize: 13,
    fontWeight: 800,
    background: 'rgba(255,255,255,.2)',
    border: '1px solid rgba(255,255,255,.35)',
    borderRadius: 99,
    padding: '4px 12px',
    marginBottom: 14,
    '& i': {
      width: 7,
      height: 7,
      borderRadius: '50%',
      background: LIME,
      boxShadow: `0 0 10px ${LIME}`,
      animation: '$proTrialPulse 1.2s infinite',
    },
  },
  mascot: {
    display: 'block',
    width: 150,
    height: 'auto',
    margin: '0 auto -10px',
    filter: 'drop-shadow(0 14px 20px rgba(120,0,30,.3))',
    animation: '$proTrialBob 4.5s ease-in-out infinite',
  },
  num: {
    position: 'relative',
    width: 112,
    height: 112,
    margin: '0 auto',
    borderRadius: '50%',
    background: '#fff',
    color: PINK,
    display: 'grid',
    placeContent: 'center',
    boxShadow: '0 18px 40px -16px rgba(120,0,30,.55)',
    '& strong': { fontSize: 54, fontWeight: 900, lineHeight: 1 },
    '& span': { fontSize: 13, fontWeight: 700, color: INK, marginTop: 2 },
  },
  hot: {
    '&:before, &:after': {
      content: '""',
      position: 'absolute',
      inset: -8,
      borderRadius: '50%',
      border: '2px solid rgba(255,255,255,.8)',
      animation: '$proTrialRing 1.8s ease-out infinite',
    },
    '&:after': { animationDelay: '-.9s' },
  },
  body: {
    padding: '24px 28px 26px',
    textAlign: 'center',
    '@media (max-width: 680px)': { padding: '20px 18px' },
  },
  title: {
    fontSize: 25,
    fontWeight: 900,
    color: INK,
    lineHeight: 1.15,
    margin: '0 0 10px',
    '@media (max-width: 680px)': { fontSize: 22 },
  },
  text: {
    fontSize: 15.5,
    color: MUTED,
    lineHeight: 1.55,
    margin: 0,
    '& b': { color: INK },
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 7,
    margin: '16px 0 4px',
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    fontWeight: 700,
    color: INK,
    background: SOFT,
    borderRadius: 99,
    padding: '6px 12px',
  },
  mark: {
    width: 20,
    height: 20,
    borderRadius: '50%',
    background: '#A3A3AE',
    color: '#fff',
    display: 'grid',
    placeItems: 'center',
    flex: 'none',
    '& svg': { width: 11, height: 11 },
  },
  markOn: { background: GRAD },
  two: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
    marginTop: 16,
    textAlign: 'start',
    '@media (max-width: 680px)': { gridTemplateColumns: '1fr' },
    '& > div': { borderRadius: 16, padding: '12px 14px', background: SOFT },
    '& small': { display: 'block', fontSize: 12, fontWeight: 800, color: MUTED, marginBottom: 6 },
    '& ul': { margin: 0, padding: 0 },
    '& li': {
      listStyle: 'none',
      display: 'flex',
      alignItems: 'center',
      gap: 7,
      fontSize: 13.5,
      fontWeight: 600,
      color: INK,
      padding: '3px 0',
    },
  },
  twoOk: {
    background: 'linear-gradient(90deg, rgba(255,0,118,.07), rgba(255,77,42,.07)) !important',
    '& small': { color: `${PINK} !important` },
  },
  smallMark: { width: 18, height: 18, '& svg': { width: 10, height: 10 } },
  acts: { display: 'grid', gap: 6, marginTop: 22 },
  cta: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    width: '100%',
    padding: '15px 24px',
    borderRadius: 500,
    border: 0,
    fontFamily: 'inherit',
    fontWeight: 700,
    fontSize: 17,
    color: '#fff',
    background: GRAD,
    boxShadow: '0 12px 30px -10px rgba(255,0,84,.7)',
    cursor: 'pointer',
    transition: '.25s',
    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 18px 38px -10px rgba(255,0,84,.8)' },
    '& svg': { width: 18, height: 18 },
  },
  arrowLtr: { transform: 'scaleX(-1)' },
  sec: {
    background: 'none',
    border: 0,
    fontFamily: 'inherit',
    color: MUTED,
    fontWeight: 600,
    fontSize: 15,
    padding: 10,
    cursor: 'pointer',
    borderRadius: 99,
    transition: '.2s',
    '&:hover': { color: INK, background: SOFT },
  },
  '@media (prefers-reduced-motion: reduce)': {
    paper: { animation: 'none !important' },
    mascot: { animation: 'none' },
  },
  // Laptop / 1080p at 100% zoom (browser window under ~880px tall): same design, tighter, so it fits without scrolling
  '@media (max-height: 880px)': {
    header: { padding: '18px 24px 14px' },
    tag: { marginBottom: 10 },
    mascot: { width: 104, margin: '0 auto -6px' },
    num: { width: 92, height: 92, '& strong': { fontSize: 44 } },
    body: { padding: '16px 24px 18px' },
    title: { fontSize: 22, marginBottom: 8 },
    text: { fontSize: 14.5, lineHeight: 1.5 },
    chips: { margin: '12px 0 2px', gap: 6 },
    chip: { padding: '5px 11px' },
    two: { marginTop: 12 },
    acts: { marginTop: 14, gap: 2 },
    cta: { padding: '12px 22px', fontSize: 16 },
    sec: { padding: 8, fontSize: 14.5 },
  },
}));

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);
const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="11" width="14" height="10" rx="2.5" />
    <path d="M8 11V7.5a4 4 0 018 0V11" />
  </svg>
);
const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);
// Points left = "forward" in RTL; flipped for LTR (as in the design)
const ArrowIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5M11 18l-6-6 6-6" />
  </svg>
);

const DATE_TOKEN = '%%PROTRIAL_DATE%%';

interface ReminderView {
  reminder: ProTrialReminder;
  days: number;
  endDate: string | null;
}

// Other screens open their own MUI dialogs on load (dashboard: terms of use, business sector, password change).
// The trial pop-up waits until none is open, so it never stacks on top of them.
const isAnotherDialogOpen = (): boolean => {
  try {
    return document.querySelectorAll('.MuiDialog-root').length > 0;
  } catch (e) {
    return false;
  }
};
const DIALOG_CHECK_MS = 1000;

const ProTrialReminders = ({ classes }: { classes: any }) => {
  const { t } = useTranslation();
  const css = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentPlan } = useSelector((state: any) => state.tiers);
  const { userRoles, isAdmin, isDebtAccount, isRTL } = useSelector((state: any) => state.core);
  const { subAccount, accountSettings } = useSelector((state: any) => state.common);
  const proTrialInfo = useSelector(selectProTrialInfo);
  const proTrialLoaded = useSelector(selectProTrialLoaded);

  // Read on the first render, before ProTrialPlansLauncher consumes it: an email deep link opens the plans
  // popup on this load, so no reminder is shown (and none is marked seen) this time.
  const [isDeepLinkLoad] = useState<boolean>(() => hasOpenPlansRequest());
  const [view, setView] = useState<ReminderView | null>(null);
  // A decided reminder waiting for the screen to be free of other dialogs
  const [pending, setPending] = useState<{ accountKey: string; memory: ProTrialReminderMemory | null; view: ReminderView } | null>(null);
  const [showTierPlans, setShowTierPlans] = useState(false);
  const decidedRef = useRef(false);

  // getCurrentPlan has answered (it fills both tiers.currentPlan and proTrial) and the sub-account is known.
  const isReady = proTrialLoaded && !!currentPlan?.Id && !!subAccount && !!accountSettings;

  useEffect(() => {
    if (!isReady || decidedRef.current) return;
    decidedRef.current = true;   // once per app load ("first login")
    if (isDeepLinkLoad) return;

    // Same gate as ProTrialPlansLauncher / the BillingSettings menu item: the plans window needs a company admin.
    const isCompanyAdmin = !!subAccount?.CompanyAdmin && userRoles === UserRoles.Admin;
    const isTermsForced = !isAdmin && !accountSettings?.SubAccountSettings?.IsTermsApproved &&
      accountSettings?.SubAccountSettings?.IgnoranceCount === 3 && getCookie('ignoreTerm') !== 'true';
    const isBlocked = (!isAdmin && isDebtAccount === true) || isTermsForced || currentPlan?.Name === 'GRAND_FATHER';
    if (!isCompanyAdmin || isBlocked) return;

    const accountKey = getReminderAccountKey(subAccount);
    if (!accountKey) return;

    const decision = decideProTrialReminder(proTrialInfo, currentPlan.Id, readReminderMemory(accountKey), Date.now(),
      currentPlan?.ProTrialExpiredEndDate ?? null);
    if (!decision.reminder) {
      writeReminderMemory(accountKey, decision.memory);
      return;
    }
    setPending({
      accountKey,
      memory: decision.memory,
      view: { reminder: decision.reminder, days: proTrialInfo?.DaysRemaining ?? 0, endDate: proTrialInfo?.EndDate ?? null },
    });
    // Decided once, when the account data is ready.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady]);

  // Show the decided reminder once no other dialog is open (first look after 2 s, so a screen's own on-load
  // dialog opens first). It is marked seen only when it really shows; leaving the page before that = next load.
  useEffect(() => {
    if (!pending) return undefined;
    let timer: number | undefined;
    let shown = false;
    const tryShow = () => {
      if (shown || isAnotherDialogOpen()) return;
      shown = true;
      if (timer !== undefined) window.clearInterval(timer);
      writeReminderMemory(pending.accountKey, pending.memory);
      setPending(null);
      setView(pending.view);
    };
    const first = window.setTimeout(() => {
      tryShow();
      if (!shown) timer = window.setInterval(tryShow, DIALOG_CHECK_MS);
    }, DIALOG_CHECK_MS * 2);
    return () => {
      window.clearTimeout(first);
      if (timer !== undefined) window.clearInterval(timer);
    };
  }, [pending]);

  if (showTierPlans) {
    return <TierPlans classes={classes} isOpen={showTierPlans} onClose={() => setShowTierPlans(false)} />;
  }
  if (!view) return null;

  const { reminder } = view;
  const key = `billing.tier.proTrialReminders.${reminder}`;
  const list = (path: string): string[] => {
    const value: any = t(`${key}.${path}`, { returnObjects: true });
    return Array.isArray(value) ? value : [];
  };

  const close = () => setView(null);
  // Welcome: "Let's get started" goes to the app home (dashboard; Swapnil 03-Nov); the other three open the plans window.
  const onPrimary = () => {
    setView(null);
    if (reminder !== 'welcome') {
      setShowTierPlans(true);
    } else if (sitePrefix && location.pathname !== sitePrefix) {
      navigate(sitePrefix);
    }
  };

  // The date is bold in the design: split the translated body around a token.
  const formattedDate = view.endDate ? moment(view.endDate).format(DateFormats.DATE_ONLY) : '';
  const bodyParts: string[] = String(t(`${key}.body`, { date: DATE_TOKEN, days: view.days })).split(DATE_TOKEN);

  const renderChips = (items: string[], done: boolean) => (
    <div className={css.chips}>
      {items.map((item) => (
        <span key={item} className={css.chip}>
          <span className={clsx(css.mark, done && css.markOn)}>{done ? <CheckIcon /> : <LockIcon />}</span>
          {item}
        </span>
      ))}
    </div>
  );

  return (
    <Dialog
      open={true}
      onClose={close}
      disableEnforceFocus
      PaperProps={{ className: clsx(css.paper, reminder === 'lastDay' && css.urgent) }}
      BackdropProps={{ style: { background: 'rgba(29,27,44,.42)', backdropFilter: 'blur(3px)' } }}
    >
      <div dir={isRTL ? 'rtl' : 'ltr'}>
        <button type="button" className={css.close} aria-label="close" onClick={close}>
          <CloseIcon />
        </button>

        <div className={css.header}>
          <span className={css.tag}><i />{t(`${key}.tag`)}</span>
          {(reminder === 'welcome' || reminder === 'ended') && <img className={css.mascot} src={proMascot} alt="" />}
          {reminder === 'endsSoon' && (
            <div className={css.num}><strong>{view.days}</strong><span>{t(`${key}.unit`)}</span></div>
          )}
          {reminder === 'lastDay' && (
            <div className={clsx(css.num, css.hot)}><strong>1</strong><span>{t(`${key}.unit`)}</span></div>
          )}
        </div>

        <div className={css.body}>
          <h3 className={css.title}>{t(`${key}.title`, { days: view.days })}</h3>
          <p className={css.text}>
            {bodyParts.map((part, i) => (
              <React.Fragment key={i}>
                {i > 0 && <b>{formattedDate}</b>}
                {part}
              </React.Fragment>
            ))}
          </p>

          {reminder === 'welcome' && renderChips(list('features'), true)}
          {(reminder === 'endsSoon' || reminder === 'lastDay') && renderChips(list('features'), false)}
          {reminder === 'ended' && (
            <div className={css.two}>
              <div className={css.twoOk}>
                <small>{t(`${key}.savedTitle`)}</small>
                <ul>
                  {list('saved').map((item) => (
                    <li key={item}><span className={clsx(css.mark, css.smallMark, css.markOn)}><CheckIcon /></span>{item}</li>
                  ))}
                </ul>
              </div>
              <div>
                <small>{t(`${key}.lockedTitle`)}</small>
                <ul>
                  {list('locked').map((item) => (
                    <li key={item}><span className={clsx(css.mark, css.smallMark)}><LockIcon /></span>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <div className={css.acts}>
            <button type="button" className={css.cta} onClick={onPrimary}>
              {t(`${key}.primary`)}
              <ArrowIcon className={isRTL ? undefined : css.arrowLtr} />
            </button>
            <button type="button" className={css.sec} onClick={close}>
              {t(`${key}.secondary`)}
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default ProTrialReminders;
